import React, { useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { initializeDatabase } from '../../lib/database';
import { isSupabaseConfigured } from '../../lib/supabase';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertTriangle, FiCheck, FiDatabase, FiExternalLink, FiRefreshCw, FiSave, FiX } from 'react-icons/fi';


const DatabaseSettings = () => {
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState({
    supabaseUrl: settings.supabaseUrl || '',
    supabaseAnonKey: settings.supabaseAnonKey || ''
  });
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supabaseUrl || !formData.supabaseAnonKey) {
      toast.error('Please provide both Supabase URL and API key');
      return;
    }

    // Update settings
    updateSettings(formData);
    
    // Test connection
    await testConnection();
    
    toast.success('Database settings saved successfully');
  };

  const testConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);

    try {
      // Update the supabase.js file would need to be done manually
      // For now, we'll simulate the test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const isConfigured = formData.supabaseUrl && formData.supabaseAnonKey;
      
      if (isConfigured) {
        setConnectionStatus('success');
        toast.success('Database connection successful!');
      } else {
        setConnectionStatus('error');
        toast.error('Invalid database credentials');
      }
    } catch (error) {
      setConnectionStatus('error');
      toast.error('Failed to connect to database');
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setConnectionStatus(null);
  };

  const isConnected = isSupabaseConfigured();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Database Configuration</h3>
        <p className="text-gray-600">
          Connect your Supabase database to enable data persistence and real-time features.
        </p>
      </div>

      {/* Current Status */}
      <div className={`mb-6 p-4 rounded-lg border ${
        isConnected 
          ? 'bg-green-50 border-green-200' 
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center gap-3">
          <SafeIcon 
            icon={isConnected ? FiCheck : FiAlertTriangle} 
            className={isConnected ? 'text-green-600' : 'text-yellow-600'} 
          />
          <div>
            <h4 className={`font-medium ${isConnected ? 'text-green-900' : 'text-yellow-900'}`}>
              {isConnected ? 'Database Connected' : 'Database Not Connected'}
            </h4>
            <p className={`text-sm ${isConnected ? 'text-green-700' : 'text-yellow-700'}`}>
              {isConnected 
                ? 'Your CRM is connected to Supabase and data is being persisted.'
                : 'Currently using mock data. Connect Supabase to persist your data.'
              }
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Supabase URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supabase Project URL *
          </label>
          <input
            type="url"
            name="supabaseUrl"
            value={formData.supabaseUrl}
            onChange={handleChange}
            placeholder="https://your-project.supabase.co"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-sm text-gray-500">
            Find this in your Supabase project settings
          </p>
        </div>

        {/* Supabase Anon Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supabase Anon Key *
          </label>
          <input
            type="password"
            name="supabaseAnonKey"
            value={formData.supabaseAnonKey}
            onChange={handleChange}
            placeholder="Your anon/public key"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-sm text-gray-500">
            Your project's anon/public key from API settings
          </p>
        </div>

        {/* Connection Status */}
        {connectionStatus && (
          <div className={`p-3 rounded-lg ${
            connectionStatus === 'success' 
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              <SafeIcon 
                icon={connectionStatus === 'success' ? FiCheck : FiX} 
                className="text-sm"
              />
              <span className="text-sm font-medium">
                {connectionStatus === 'success' 
                  ? 'Connection successful!' 
                  : 'Connection failed'
                }
              </span>
            </div>
          </div>
        )}

        {/* Setup Instructions */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-3">Setup Instructions</h4>
          <div className="space-y-2 text-sm text-blue-700">
            <div className="flex items-start gap-2">
              <span className="font-medium">1.</span>
              <div>
                Create a free Supabase account at{' '}
                <a 
                  href="https://supabase.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  supabase.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium">2.</span>
              <span>Create a new project</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium">3.</span>
              <span>Copy your project URL and anon key from Settings → API</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium">4.</span>
              <span>Paste them above and save settings</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium">5.</span>
              <span>Run the Supabase SQL migration from the README to create the Prosper CRM tables before using the app</span>
            </div>
          </div>
        </div>

        {/* Database Schema Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Database Schema</h4>
          <p className="text-sm text-gray-700 mb-3">
            The following tables will be created automatically:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiDatabase} className="text-gray-400 text-xs" />
              <span>contacts_prosper</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiDatabase} className="text-gray-400 text-xs" />
              <span>activities_prosper</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiDatabase} className="text-gray-400 text-xs" />
              <span>notes_prosper</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiDatabase} className="text-gray-400 text-xs" />
              <span>tasks_prosper</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiDatabase} className="text-gray-400 text-xs" />
              <span>meetings_prosper</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={testConnection}
            disabled={testing || !formData.supabaseUrl || !formData.supabaseAnonKey}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SafeIcon icon={testing ? FiRefreshCw : FiDatabase} className={`text-sm ${testing ? 'animate-spin' : ''}`} />
            {testing ? 'Testing...' : 'Test Connection'}
          </button>

          <button
            type="submit"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Settings
          </button>

          <a
            href="https://supabase.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium ml-auto"
          >
            Get Supabase Account
            <SafeIcon icon={FiExternalLink} className="text-xs" />
          </a>
        </div>
      </form>
    </div>
  );
};

export default DatabaseSettings;